import { AddPhoneRequest, GetUserResponse } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getAppCheckLimitedUseTokenHeaders, isFirebaseError } from '@/firebase';
import {
    getFallbackErrorKey,
    getRetryAfterSeconds,
    isApiError
} from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';

type UseAddCompanyUserPhoneActionProps = {
    /**
     * Which of the member's two numbers this call is for — `primary` is
     * `phone`, `secondary` is `phone2`. The two endpoints are the same call
     * against a different field, so the slot is fixed for the hook's lifetime
     * rather than chosen per call.
     */
    slot: 'primary' | 'secondary';
};

/** The member the number belongs to, and the number itself. */
type AddPhoneVariables = {
    companyUserId: string;
    data: AddPhoneRequest;
};

type UseAddCompanyUserPhoneActionData = {
    addPhone: (
        companyUserId: string,
        data: AddPhoneRequest
    ) => Promise<GetUserResponse>;
    loading: boolean;
    error: string;
};

/**
 * Sending a verification code to a phone number a member wants to add. The
 * number is not stored against them until `useVerifyCompanyUserPhoneAction`
 * confirms the code, so there is no cache to update on success.
 *
 * Carries no `isAllowed` of its own: whether the viewer may edit this member is
 * `useEditProfileAction`, which gates the whole page of sections.
 */
export function useAddCompanyUserPhoneAction(
    props: UseAddCompanyUserPhoneActionProps
): UseAddCompanyUserPhoneActionData {
    const { slot } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/users/{id}/phone` and `POST /v3/users/{id}/phone2` declare gets
    // a branch — the two share one error union.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            const seconds = getRetryAfterSeconds(error);

            if (
                isFirebaseError(error) ||
                isApiError(error, 'APP_CHECK_FAILED')
            ) {
                return t('shared.errors.app_check_failed');
            }
            if (isApiError(error, 'TOO_MANY_REQUESTS') && seconds !== null) {
                return t('shared.errors.too_many_verification_codes', {
                    seconds
                });
            }
            if (isApiError(error, 'TOO_MANY_REQUESTS')) {
                return t('shared.errors.too_many_requests');
            }

            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return t('shared.errors.user_not_found');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: ({ companyUserId, data }: AddPhoneVariables) =>
            // The limited-use token, not the cached one: sending a code costs
            // money, and a resend is a second call a replayed token would fail.
            getAppCheckLimitedUseTokenHeaders()
                .then((headers) =>
                    slot === 'secondary'
                        ? openApiRef.current.users.addUserPhone2(
                              companyUserId,
                              data,
                              { headers }
                          )
                        : openApiRef.current.users.addUserPhone(
                              companyUserId,
                              data,
                              { headers }
                          )
                )
                .then((response) => response.data),
        // Nothing to write to the cache: the number is not on the member until
        // the code that follows has been verified.
        onSuccess: () => {
            UIToast.success(t('shared.toasts.verification_code_sent'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        addPhone: (
            companyUserId: string,
            data: AddPhoneRequest
        ): Promise<GetUserResponse> =>
            mutation.mutateAsync({ companyUserId, data }),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
